import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Truck, Plus } from "lucide-react";

import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";

import DataTable from "@/components/mui/DataTable";

const onlyDigits = (value = "") => value.replace(/\D/g, "");

const formatCnpj = (value = "") => {
  const digits = onlyDigits(value).slice(0, 14);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const formatPhone = (value = "") => {
  const digits = onlyDigits(value).slice(0, 11);
  if (!digits) return "";
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [totalSuppliers, setTotalSuppliers] = useState(0);

  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);

  // paginação
  const [page, setPage] = useState(0); // zero-based
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // filtro simples de busca (name / cnpj)
  const [search, setSearch] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    cnpj: "",
    phone: "",
    email: "",
    address: "",
  });

  useEffect(() => {
    fetchSuppliers(0, rowsPerPage, "");
  }, []);

  const fetchSuppliers = async (
    pageIndex = 0,
    perPage = rowsPerPage,
    searchTerm = search
  ) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/suppliers`, {
        params: {
          page: pageIndex + 1, // backend 1-based
          per_page: perPage,
          search: searchTerm || undefined,
        },
      });

      const data = response.data;
      if (data && data.data) {
        setSuppliers(data.data);
        setTotalSuppliers(data.total || data.data.length);
      } else {
        const list = data || [];
        setSuppliers(list);
        setTotalSuppliers(list.length);
      }
    } catch (error) {
      toast.error("Erro ao carregar fornecedores");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (supplier = null) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setFormData({
        name: supplier.name || "",
        cnpj: formatCnpj(supplier.cnpj || ""),
        phone: formatPhone(supplier.phone || ""),
        email: supplier.email || "",
        address: supplier.address || "",
      });
    } else {
      setEditingSupplier(null);
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSupplier(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: "",
      cnpj: "",
      phone: "",
      email: "",
      address: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        cnpj: onlyDigits(formData.cnpj),
        phone: onlyDigits(formData.phone),
      };

      if (editingSupplier) {
        await axios.put(
          `${API}/suppliers/${editingSupplier.id}`,
          payload
        );
        toast.success("Fornecedor atualizado com sucesso!");
      } else {
        await axios.post(`${API}/suppliers`, payload);
        toast.success("Fornecedor criado com sucesso!");
      }

      handleCloseModal();
      fetchSuppliers(page, rowsPerPage, search);
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Erro ao salvar fornecedor"
      );
    }
  };

  const handleDelete = async (supplier) => {
    const confirm = window.confirm(
      `Deseja realmente excluir o fornecedor "${supplier.name}"?`
    );
    if (!confirm) return;

    try {
      await axios.delete(`${API}/suppliers/${supplier.id}`);
      toast.success("Fornecedor excluído com sucesso!");
      fetchSuppliers(page, rowsPerPage, search);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        "Erro ao excluir fornecedor"
      );
    }
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
    fetchSuppliers(newPage, rowsPerPage, search);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRows = parseInt(event.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0);
    fetchSuppliers(0, newRows, search);
  };

  const handleSearch = () => {
    setPage(0);
    fetchSuppliers(0, rowsPerPage, search);
  };

  if (loading && suppliers.length === 0) {
    return (
      <Box
        sx={{
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const columns = [
    {
      id: "name",
      label: "Nome",
      render: (supplier) => (
        <Typography fontWeight={600}>{supplier.name}</Typography>
      ),
    },
    {
      id: "cnpj",
      label: "CNPJ",
      render: (supplier) => formatCnpj(supplier.cnpj || "") || "-",
    },
    {
      id: "phone",
      label: "Telefone",
      render: (supplier) => formatPhone(supplier.phone || "") || "-",
    },
    {
      id: "email",
      label: "Email",
      render: (supplier) => supplier.email || "-",
    },
    {
      id: "products_count",
      label: "Produtos vinculados",
      align: "center",
      render: (supplier) => supplier.products_count ?? 0,
    },
    {
      id: "actions",
      label: "Ações",
      align: "right",
      render: (supplier) => (
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleOpenModal(supplier)}
          >
            Editar
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => handleDelete(supplier)}
          >
            Excluir
          </Button>
        </Box>
      ),
    },
  ];

  const tablePagination = {
    page,
    rowsPerPage,
    total: totalSuppliers,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [5, 10, 25, 50],
  };

  return (
    <Box data-testid="suppliers-page">
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        mb={4}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Truck size={32} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Fornecedores
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cadastro e gerenciamento de fornecedores
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => handleOpenModal(null)}
          data-testid="add-supplier-btn"
        >
          Novo Fornecedor
        </Button>
      </Box>

      {/* Filtro de busca */}
      <Paper className="data-table-container">
        <Box
          className="table-header"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            gap: 2,
            px: 3,
            py: 2,
          }}
        >
          <TextField
            label="Buscar por nome ou CNPJ"
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            sx={{ minWidth: 140 }}
          >
            Buscar
          </Button>
        </Box>

        {/* Tabela */}
        {suppliers.length === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 1.5,
            }}
          >
            <Truck size={64} />
            <Typography variant="h6">
              Nenhum fornecedor cadastrado
            </Typography>
            <Typography variant="body2">
              Adicione fornecedores para vincular às compras.
            </Typography>
          </Box>
        ) : (
          <DataTable
            columns={columns}
            rows={suppliers}
            loading={loading}
            pagination={tablePagination}
            getRowId={(row) => row.id}
            getRowTestId={(row) => `supplier-row-${row.id}`}
          />
        )}
      </Paper>

      {/* Dialog de criação/edição */}
      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingSupplier ? "Editar Fornecedor" : "Novo Fornecedor"}
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Nome"
                fullWidth
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />

              <TextField
                label="CNPJ"
                fullWidth
                value={formData.cnpj}
                onChange={(e) =>
                  setFormData({ ...formData, cnpj: formatCnpj(e.target.value) })
                }
                placeholder="00.000.000/0000-00"
                inputProps={{ maxLength: 18 }}
              />

              <TextField
                label="Telefone"
                fullWidth
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: formatPhone(e.target.value) })
                }
                placeholder="(00) 00000-0000"
                inputProps={{ maxLength: 15 }}
              />

              <TextField
                label="Email"
                type="email"
                fullWidth
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />

              <TextField
                label="Endereço"
                fullWidth
                multiline
                minRows={2}
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
              />
            </Box>

            <DialogActions sx={{ mt: 3 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={handleCloseModal}
              >
                Cancelar
              </Button>
              <Button type="submit" variant="contained">
                {editingSupplier ? "Atualizar" : "Criar"}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
